import { useCallback, useEffect, useState } from 'react'
import { ganadorAlTiempo, tiempoConcluido } from '@domino/core'
import { useCarga } from '../carga.js'
import { obtenerRepositorio } from '../datos/index.js'
import type { JugadorEnMesa, PartidaEnVivo } from '../datos/index.js'
import { plural } from '../formato.js'
import { ESPERA_CACHE_PUBLICA_MS, useAvisosDelTorneo, useLlamadaEspaciada } from '../tiempo-real.js'
import { Cargando, Problema } from './Estados.js'
import { Icono } from './Icono.js'
import { Cuaderno, Lado } from './Libreta.js'

/**
 * La partida de una mesa, mano a mano, para mirarla.
 *
 * Tiene la misma cara que la libreta de anotación (el marcador grande y la hoja
 * rayada con una columna por bando) para que el jugador no tenga que leer dos
 * planillas distintas según desde dónde entró. Acá no se escribe: se anota en la
 * libreta, que tiene pantalla propia. Al que está sentado en la mesa se le
 * ofrece abrirla con un toque; al que mira desde otra mesa, o desde su casa por
 * la vitrina, solo se le muestra cómo va. Qué es cada uno no lo decide la
 * pantalla: lo dice el API en `puedoCargar`.
 *
 * Se entera sola de lo que anota la mesa: escucha los avisos del torneo por el
 * socket (ver `tiempo-real.ts`) y vuelve a pedir la partida en cuanto llega uno
 * de ESTA mesa. La miran pocos (los cuatro de la mesa y quien la eligió para
 * seguirla), así que puede reaccionar a cada mano sin freno.
 *
 * Por si el socket no conecta, mientras la partida se juega igual se vuelve a
 * pedir cada tanto. Es el respaldo, no el mecanismo: con el socket andando,
 * casi siempre llega sin cambios.
 */

/** El respaldo del socket: cada cuánto se vuelve a pedir mientras se juega. */
const RESPALDO_MS = 60_000

/** Cómo se nombra cada bando: «Nosotros» y «Ellos» para el que está sentado. */
export function etiquetas(jugadores: readonly JugadorEnMesa[]): {
  miBando: 1 | 2 | null
  nombre: Record<1 | 2, string>
  /** Los nombres de pila de cada bando, para arriba del marcador. */
  quienes: Record<1 | 2, string>
} {
  const mio = jugadores.find((j) => j.esMio)?.bando ?? null
  const nombres = (b: 1 | 2) =>
    jugadores
      .filter((j) => j.bando === b)
      .map((j) => j.nombre.split(' ')[0])
      .join(' y ') || `Bando ${b}`

  const quienes = { 1: nombres(1), 2: nombres(2) }
  if (mio === null) return { miBando: null, nombre: quienes, quienes }
  return {
    miBando: mio,
    nombre: mio === 1 ? { 1: 'Nosotros', 2: 'Ellos' } : { 1: 'Ellos', 2: 'Nosotros' },
    quienes,
  }
}

/** Lo que se lee al pie de la libreta: en qué quedó la partida. */
export function estadoDe(partida: PartidaEnVivo, nombre: Record<1 | 2, string>): string {
  if (partida.mesaEstado === 'cargada') {
    return 'La mesa ya tiene su resultado oficial: la libreta quedó cerrada.'
  }
  if (partida.concluida && partida.ganador !== null) {
    const quien = nombre[partida.ganador]
    const gano =
      quien === 'Nosotros' ? 'Ganamos' : quien === 'Ellos' ? 'Ganaron ellos' : `Ganó ${quien}`
    return partida.modo === 'sugerida'
      ? `${gano}. El marcador le quedó propuesto al organizador, que lo confirma.`
      : `${gano}. El resultado oficial lo carga el organizador.`
  }
  if (tiempoConcluido(partida.venceEn)) {
    return ganadorAlTiempo(partida) === null
      ? 'Tiempo concluido y van empatados: se juega hasta que uno quede arriba.'
      : 'Tiempo concluido. Anoten la mano que estaban jugando: con ella se cierra la mesa.'
  }
  return `Se juega a ${partida.objetivoPuntos} puntos.`
}

/**
 * La partida de una mesa, al día: la pide, escucha los avisos de esa mesa y
 * tiene el respaldo por si el socket no conecta. La usan la planilla y la
 * libreta de anotación, que son dos maneras de dibujar lo mismo.
 */
export function usePartidaDeMesa(torneoId: string, mesaId: string, publico = false) {
  const pedir = useCallback(async () => {
    const repo = await obtenerRepositorio()
    return publico ? repo.partidaDeMesaPublica(torneoId, mesaId) : repo.partidaDeMesa(torneoId, mesaId)
  }, [torneoId, mesaId, publico])

  const { datos, error, cargando, recargar, refrescar } = useCarga(pedir)

  /* Desde la vitrina se espera a que venza la copia del borde: pedir apenas
     llega el aviso puede traer la partida de antes de la mano. */
  const trasLaCache = useLlamadaEspaciada(refrescar, 0, ESPERA_CACHE_PUBLICA_MS)
  useAvisosDelTorneo(torneoId, publico, (aviso) => {
    if (aviso.tipo === 'mesa' && aviso.mesaId !== mesaId) return
    if (aviso.tipo === 'ronda') return
    if (publico) trasLaCache()
    else refrescar()
  })

  /* Lo que contestó la última escritura. Manda sobre lo pedido hasta que llega
     el próximo pedido, que ya lo trae incluido. */
  const [escrita, setEscrita] = useState<PartidaEnVivo | null>(null)
  useEffect(() => setEscrita(null), [datos])
  const partida = escrita ?? datos

  const enJuego =
    partida !== null &&
    partida.modo !== 'desactivada' &&
    partida.mesaEstado === 'pendiente' &&
    !partida.concluida

  /* El respaldo: solo con la partida en juego y la pestaña a la vista. Un
     teléfono en el bolsillo no tiene por qué seguir preguntando, y al volver a
     mirarlo se pide una vez, por si el socket se cortó mientras tanto. */
  useEffect(() => {
    if (!enJuego) return
    const tic = window.setInterval(() => {
      if (document.visibilityState === 'visible') refrescar()
    }, RESPALDO_MS)
    const alVolver = () => {
      if (document.visibilityState === 'visible') refrescar()
    }
    document.addEventListener('visibilitychange', alVolver)
    return () => {
      window.clearInterval(tic)
      document.removeEventListener('visibilitychange', alVolver)
    }
  }, [enJuego, refrescar])

  return { partida, error, cargando, recargar, alEscribir: setEscrita }
}

export function PlanillaDeMesa({
  torneoId,
  mesaId,
  publico = false,
  alAnotar,
}: {
  torneoId: string
  mesaId: string
  /** Desde la vitrina: pide por la ruta sin cuenta y nunca ofrece anotar. */
  publico?: boolean
  /** Abre la libreta de anotación de esta mesa. Solo se ofrece si `puedoCargar`. */
  alAnotar?: () => void
}) {
  const { partida, error, cargando, recargar } = usePartidaDeMesa(torneoId, mesaId, publico)

  if (error && !partida) return <Problema mensaje={error} alReintentar={recargar} />
  if (!partida) return cargando ? <Cargando que="Buscando la planilla de la mesa…" /> : null
  if (partida.modo === 'desactivada' && partida.manos.length === 0) return null

  return (
    <Planilla
      partida={partida}
      alRefrescar={recargar}
      refrescando={cargando}
      {...(alAnotar && !publico ? { alAnotar } : {})}
    />
  )
}

/**
 * La planilla ya cargada. Separada de quien la pide para poder dibujarla (y
 * probarla) con una partida escrita a mano.
 */
export function Planilla({
  partida,
  alRefrescar,
  refrescando = false,
  alAnotar,
}: {
  partida: PartidaEnVivo
  alRefrescar?: () => void
  refrescando?: boolean
  alAnotar?: () => void
}) {
  const { miBando, nombre, quienes } = etiquetas(partida.jugadores)
  /* El bando propio va a la izquierda: es el que se busca primero. Sin bando
     propio (mirando de afuera) el orden es el de la mesa. */
  const orden: [1 | 2, 1 | 2] = miBando === 2 ? [2, 1] : [1, 2]
  const anotable = alAnotar !== undefined && partida.puedoCargar && partida.mesaEstado === 'pendiente'

  return (
    <div className="libreta planilla">
      <div className="libreta-marcador" aria-live="polite">
        <Lado partida={partida} bando={orden[0]} nombre={quienes[orden[0]]} />
        <div className="libreta-a">
          a<b>{partida.objetivoPuntos}</b>
        </div>
        <Lado partida={partida} bando={orden[1]} nombre={quienes[orden[1]]} derecha />
      </div>

      {partida.manos.length === 0 && partida.restas.length === 0 ? (
        <p className="libreta-vacia">Todavía no se anotó ninguna mano.</p>
      ) : (
        <Cuaderno partida={partida} orden={orden} quienes={quienes} elegida={null} />
      )}

      {/* El acceso a anotar, a un toque: la libreta tiene pantalla propia. */}
      {anotable && (
        <button className="boton principal mesa-anotar" onClick={alAnotar}>
          {partida.concluida ? 'Abrir la libreta para corregir' : 'Abrir la libreta de anotación'}
        </button>
      )}

      <footer className="planilla-pie">
        <span>{estadoDe(partida, nombre)}</span>
        {alRefrescar && (
          <button
            className="mesas-refrescar"
            onClick={alRefrescar}
            disabled={refrescando}
            title="Volver a pedir la planilla"
          >
            <Icono nombre="refrescar" tamano={15} />
            Actualizar
          </button>
        )}
      </footer>
    </div>
  )
}

/** Para la tarjeta de la lista: «3 manos». */
export function manosAnotadas(cantidad: number): string {
  return plural(cantidad, 'mano', 'manos')
}
